import React, { useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  AccountTree,
  Business,
  Check,
  Close,
  Groups,
  Person,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import {
  IKiBeteiligter,
  IKiGesellschaftsanteil,
  IKiGesellschafterliste,
  IKiGesellschafterlisteResult,
  IKiKontrollGesellschafter,
  IKiOwnerDecision,
} from '../Interfaces/IKi';
import {
  useGesellschafterlisteAuslesen,
  useGesellschafterlisteUebernehmen,
} from '../hooks/reactQuerys/KiQueries';
import {
  CONFIDENCE_COLOR,
  IDecision,
  decisionValue,
  details,
  initialDecision,
  matchLabel,
} from './KiMandantProposalCard';
import { KiKontrollstruktur, startAusListe } from './KiKontrollstruktur';
import {
  MAX_ABRUFE,
  MAX_TIEFE,
  anteileAusNennbetraegen,
} from './kontrollstruktur';

export interface IGesellschafterlisteDatei {
  FileName: string;
  MimeType: string;
  /** Base64. */
  Data: string;
}

interface IProps {
  firma: string;
  /** Die Gesellschaft, falls sie schon Mandant ist. */
  idLegalClient: number | null;
  /** Die abgerufene Liste — aus der Karte oder nachgeladen. */
  dateiHolen: () => Promise<IGesellschafterlisteDatei>;
  /** Wer die Liste liest, z. B. „Claude (Anthropic)". */
  anbieter?: string | null;
}

const euro = (wert: number) =>
  wert.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

/** „50 % · 12.500 Anteile à 1,00 €" */
const anteilText = (anteil: IKiGesellschaftsanteil): string =>
  [
    anteil.Shares !== null
      ? `${(anteil.Shares * 100).toLocaleString('de-DE', { maximumFractionDigits: 2 })} %`
      : null,
    anteil.AmountOfShares !== null
      ? `${anteil.AmountOfShares.toLocaleString('de-DE')} Anteil(e)${
          anteil.ValuePerShare !== null
            ? ` à ${euro(anteil.ValuePerShare)}`
            : ''
        }`
      : null,
    anteil.Nennbetrag !== null ? `Nennbetrag ${euro(anteil.Nennbetrag)}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

/** Für die Kontrollstruktur: so, wie das Register die Gesellschaft sucht. */
const alsKontrollEintrag = (
  b: IKiBeteiligter,
  anteil: number | null = null
): IKiKontrollGesellschafter => {
  const nummer = b.Registernummer ?? null;
  return {
    art: b.art,
    Bezeichnung: b.Bezeichnung,
    Geburtsdatum: b.Geburtsdatum ?? null,
    Ort: b.Ort ?? b.Sitz ?? null,
    Firma: b.Firma ?? (b.art === 'gesellschaft' ? b.Bezeichnung : null),
    Rechtsform: b.Rechtsform ?? null,
    Registergericht: b.Registergericht ?? null,
    // „HRB 1234" — die Liste nennt Art und Nummer mal zusammen, mal getrennt.
    Registernummer:
      nummer !== null && b.Registerart && /^\d/.test(nummer.trim())
        ? `${b.Registerart} ${nummer.trim()}`
        : nummer,
    Anteil: anteil,
  };
};

const fehlerText = (exception: unknown, rueckfall: string): string => {
  const detail =
    exception instanceof Error && exception.message
      ? exception.message.replace(/^\d{3} [^:]+: /, '')
      : '';

  return detail !== '' ? detail : rueckfall;
};

/**
 * Nach dem Abruf einer Gesellschafterliste: die Gesellschafter anlegen (#1336).
 *
 * Erst die Frage, dann das Lesen, dann die Entscheidung je Eintrag — und erst
 * der letzte Klick legt etwas an. Ist die Gesellschaft schon Mandant, wird
 * ihre Beziehung „Firma" ergänzt, nicht ersetzt: wer nicht mehr auf der Liste
 * steht, wird gemeldet, nicht entfernt.
 */
export const KiGesellschafterlistePanel: React.FC<IProps> = ({
  firma,
  idLegalClient,
  dateiHolen,
  anbieter,
}) => {
  const navigate = useNavigate();
  const { liesGesellschafterliste, isPendingLies } =
    useGesellschafterlisteAuslesen();
  const { uebernehmeGesellschafter, isPendingUebernehmeGesellschafter } =
    useGesellschafterlisteUebernehmen();

  const [state, setState] = useState<
    'frage' | 'pruefen' | 'fertig' | 'verworfen'
  >('frage');
  const [liste, setListe] = useState<IKiGesellschafterliste | null>(null);
  const [firmaEntscheidung, setFirmaEntscheidung] = useState<IDecision>({
    Entscheidung: 'neu',
    idLegalClient: null,
  });
  const [entscheidungen, setEntscheidungen] = useState<IDecision[]>([]);
  const [ergebnis, setErgebnis] = useState<IKiGesellschafterlisteResult | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [kontrolle, setKontrolle] = useState<'frage' | 'ja' | 'nein'>('frage');

  const beteiligteGesellschaften = (liste?.GesellschafterArray ?? []).filter(
    g => g.art === 'gesellschaft'
  );

  // Stehen Gesellschaften auf der Liste, fragt Karl nach der
  // Eigentümer- und Kontrollstruktur — unabhängig vom Anlegen.
  const kontrollBlock =
    liste === null ||
    beteiligteGesellschaften.length === 0 ? null : kontrolle === 'ja' ? (
      <KiKontrollstruktur
        // Nur der Anfangsstand — die Komponente liest ihn einmal.
        start={startAusListe(
          alsKontrollEintrag(liste.Gesellschaft),
          anteileAusNennbetraegen(
            liste.GesellschafterArray.map(g => ({
              ...alsKontrollEintrag(g, g.Anteil.Shares),
              Nennbetrag: g.Anteil.Nennbetrag,
            }))
          ),
          liste.Datum ? `Gesellschafterliste vom ${liste.Datum}` : null
        )}
        idLegalClient={
          idLegalClient ?? liste.Gesellschaft.idLegalClientVorschlag ?? null
        }
      />
    ) : kontrolle === 'frage' ? (
      <Paper variant='outlined' sx={{ p: 1.5, mt: 1 }}>
        <Stack spacing={1}>
          <Stack direction='row' spacing={1} alignItems='center'>
            <AccountTree fontSize='small' color='primary' />
            <Typography variant='body2'>
              In der Liste{' '}
              {beteiligteGesellschaften.length === 1 ? 'steht' : 'stehen'}{' '}
              {beteiligteGesellschaften.length === 1
                ? 'eine Gesellschaft'
                : 'Gesellschaften'}{' '}
              ({beteiligteGesellschaften.map(g => g.Bezeichnung).join(', ')}).
              Soll ich die Eigentümer- und Kontrollstruktur prüfen?
            </Typography>
          </Stack>
          <Typography variant='caption' color='text.secondary'>
            Ich rufe dazu deren Gesellschafterlisten bzw. Registerauszüge ab und
            gehe weiter, bis natürliche Personen herauskommen — höchstens{' '}
            {MAX_TIEFE} Ebenen und {MAX_ABRUFE} Abrufe. Gelesen wird von{' '}
            {anbieter ?? 'der KI'}.
          </Typography>
          <Stack direction='row' spacing={1} justifyContent='flex-end'>
            <Button
              size='small'
              color='inherit'
              onClick={() => setKontrolle('nein')}
            >
              Nein, danke
            </Button>
            <Button
              size='small'
              variant='outlined'
              startIcon={<AccountTree />}
              onClick={() => setKontrolle('ja')}
            >
              Struktur prüfen
            </Button>
          </Stack>
        </Stack>
      </Paper>
    ) : null;

  if (state === 'verworfen') {
    return kontrollBlock;
  }

  async function handleLesen() {
    setError(null);

    try {
      const datei = await dateiHolen();
      const gelesen = await liesGesellschafterliste({
        Datei: datei,
        idLegalClient,
      });

      setListe(gelesen);
      setFirmaEntscheidung(
        gelesen.Gesellschaft.Fest
          ? {
              Entscheidung: 'zusammenfuehren',
              idLegalClient:
                gelesen.Gesellschaft.idLegalClientVorschlag ?? null,
            }
          : initialDecision(gelesen.Gesellschaft)
      );
      setEntscheidungen(gelesen.GesellschafterArray.map(initialDecision));
      setState('pruefen');
    } catch (exception) {
      setError(fehlerText(exception, 'Die Liste konnte nicht gelesen werden.'));
    }
  }

  async function handleUebernehmen() {
    if (liste === null) {
      return;
    }

    setError(null);

    try {
      const result = await uebernehmeGesellschafter({
        Gesellschaft: { ...liste.Gesellschaft, ...firmaEntscheidung },
        GesellschafterArray: liste.GesellschafterArray.map((g, index) => ({
          ...g,
          ...entscheidungen[index],
        })) as unknown as Record<string, unknown>[],
      });

      setErgebnis(result);
      setState('fertig');
    } catch (exception) {
      setError(
        fehlerText(
          exception,
          'Die Gesellschafter konnten nicht übernommen werden.'
        )
      );
    }
  }

  const auswahl = (
    beteiligter: IKiBeteiligter,
    entscheidung: IDecision,
    onChange: (decision: IDecision) => void,
    schluessel: string,
    neuText: string
  ) => (
    <TextField
      select
      size='small'
      sx={{ maxWidth: 420 }}
      value={decisionValue(entscheidung)}
      onChange={event => {
        const raw = event.target.value;
        onChange(
          raw.startsWith('client:')
            ? {
                Entscheidung: 'zusammenfuehren',
                idLegalClient: Number(raw.slice('client:'.length)),
              }
            : { Entscheidung: raw as IKiOwnerDecision, idLegalClient: null }
        );
      }}
    >
      <MenuItem value='neu'>{neuText}</MenuItem>
      <MenuItem value='ignorieren'>Nicht übernehmen</MenuItem>
      {(beteiligter.MatchArray ?? []).map(match => (
        <MenuItem
          key={`${schluessel}-${match.idLegalClient}`}
          value={`client:${match.idLegalClient}`}
        >
          {`Zusammenführen: ${matchLabel(match)}`}
        </MenuItem>
      ))}
    </TextField>
  );

  if (state === 'fertig' && ergebnis !== null) {
    return (
      <Stack spacing={1} sx={{ mt: 1 }}>
        <Alert
          severity='success'
          action={
            <Button
              size='small'
              color='inherit'
              onClick={() => navigate(`/clients/${ergebnis.idGesellschaft}`)}
            >
              Zum Mandanten
            </Button>
          }
        >
          {ergebnis.BeziehungNeu
            ? 'Gesellschafter-Beziehung angelegt.'
            : 'Gesellschafter-Beziehung ergänzt.'}
          {ergebnis.AngelegtArray.length > 0 &&
            ` ${ergebnis.AngelegtArray.length} Mandant(en) neu angelegt.`}
          <Stack
            direction='row'
            spacing={0.5}
            flexWrap='wrap'
            useFlexGap
            sx={{ mt: 0.5 }}
          >
            {ergebnis.GesellschafterArray.map(g => (
              <Chip
                key={`ki-gl-fertig-${g.idLegalClient}`}
                size='small'
                label={`${g.Bezeichnung}: ${g.Status}`}
                onClick={() => navigate(`/clients/${g.idLegalClient}`)}
              />
            ))}
          </Stack>
        </Alert>
        {ergebnis.NichtMehrAufDerListeArray.length > 0 && (
          <Alert severity='warning'>
            In DiNo als Gesellschafter geführt, aber nicht mehr auf der Liste:{' '}
            {ergebnis.NichtMehrAufDerListeArray.map(g => g.Bezeichnung).join(
              ', '
            )}
            . Nichts entfernt — bitte prüfen und das Ausscheiden selbst
            eintragen.
          </Alert>
        )}
        {ergebnis.Uebersprungen.length > 0 && (
          <Alert severity='warning' sx={{ py: 0 }}>
            <Stack component='ul' sx={{ m: 0, pl: 2 }}>
              {ergebnis.Uebersprungen.map(zeile => (
                <Typography component='li' variant='caption' key={zeile}>
                  {zeile}
                </Typography>
              ))}
            </Stack>
          </Alert>
        )}
        {kontrollBlock}
      </Stack>
    );
  }

  if (state === 'frage' || liste === null) {
    return (
      <Paper variant='outlined' sx={{ p: 1.5, mt: 1 }}>
        <Stack spacing={1}>
          <Stack direction='row' spacing={1} alignItems='center'>
            <Groups fontSize='small' color='primary' />
            <Typography variant='body2'>
              {idLegalClient !== null
                ? `Soll ich ${firma} um die Gesellschafter aus der Liste ergänzen?`
                : `Soll ich ${firma} und die Gesellschafter als Mandanten anlegen?`}
            </Typography>
          </Stack>
          <Typography variant='caption' color='text.secondary'>
            Dafür liest {anbieter ?? 'die KI'} die Liste. Angelegt wird erst,
            wenn Sie jeden Eintrag geprüft haben.
          </Typography>
          {error !== null && <Alert severity='warning'>{error}</Alert>}
          <Stack direction='row' spacing={1} justifyContent='flex-end'>
            <Button
              size='small'
              color='inherit'
              disabled={isPendingLies}
              onClick={() => setState('verworfen')}
            >
              Nein, danke
            </Button>
            <Button
              size='small'
              variant='outlined'
              startIcon={
                isPendingLies ? (
                  <CircularProgress size={16} color='inherit' />
                ) : (
                  <Groups />
                )
              }
              disabled={isPendingLies}
              onClick={() => void handleLesen()}
            >
              {isPendingLies
                ? 'Liste wird gelesen …'
                : 'Gesellschafter auslesen'}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    );
  }

  const gesellschaft = liste.Gesellschaft;

  return (
    <>
      <Paper
        variant='outlined'
        sx={{ p: 1.5, mt: 1, borderColor: 'primary.light' }}
      >
        <Stack spacing={1.5}>
          <Stack direction='row' spacing={1} alignItems='center'>
            <Groups fontSize='small' color='primary' />
            <Typography variant='subtitle2' sx={{ flexGrow: 1 }}>
              Gesellschafter übernehmen
            </Typography>
            {liste.Datum !== null && (
              <Chip
                size='small'
                variant='outlined'
                label={`Stand ${liste.Datum}`}
              />
            )}
            {liste.Confidence !== null && (
              <Chip
                size='small'
                color={CONFIDENCE_COLOR[liste.Confidence]}
                variant='outlined'
                label={`Sicherheit: ${liste.Confidence}`}
              />
            )}
          </Stack>

          <Stack spacing={0.5}>
            <Stack direction='row' spacing={1} alignItems='center'>
              <Business fontSize='small' color='action' />
              <Typography variant='body2' sx={{ fontWeight: 500 }}>
                {gesellschaft.Bezeichnung}
              </Typography>
              {liste.Stammkapital !== null && (
                <Chip
                  size='small'
                  variant='outlined'
                  label={`Stammkapital ${euro(liste.Stammkapital)}`}
                />
              )}
            </Stack>
            {details(gesellschaft) !== '' && (
              <Typography variant='caption' color='text.secondary'>
                {details(gesellschaft)}
              </Typography>
            )}
            {gesellschaft.Fest ? (
              <Typography variant='caption' color='text.secondary'>
                Bereits Mandant — die Gesellschafter werden dort ergänzt.
              </Typography>
            ) : (
              auswahl(
                gesellschaft,
                firmaEntscheidung,
                setFirmaEntscheidung,
                'ki-gl-firma',
                'Gesellschaft als neuen Mandanten anlegen'
              )
            )}
          </Stack>

          <Divider />

          <Stack divider={<Divider flexItem />} spacing={1}>
            {liste.GesellschafterArray.map((g, index) => (
              <Stack key={`ki-gl-g-${index}`} spacing={0.5}>
                <Stack direction='row' spacing={1} alignItems='center'>
                  {g.art === 'gesellschaft' ? (
                    <Business fontSize='small' color='action' />
                  ) : (
                    <Person fontSize='small' color='action' />
                  )}
                  <Typography variant='body2' sx={{ fontWeight: 500 }}>
                    {g.Bezeichnung}
                  </Typography>
                  {anteilText(g.Anteil) !== '' && (
                    <Chip
                      size='small'
                      color='primary'
                      variant='outlined'
                      label={anteilText(g.Anteil)}
                    />
                  )}
                </Stack>
                {details(g) !== '' && (
                  <Typography variant='caption' color='text.secondary'>
                    {details(g)}
                  </Typography>
                )}
                {auswahl(
                  g,
                  entscheidungen[index],
                  decision =>
                    setEntscheidungen(current =>
                      current.map((e, i) => (i === index ? decision : e))
                    ),
                  `ki-gl-match-${index}`,
                  'Als neuen Mandanten anlegen'
                )}
              </Stack>
            ))}
          </Stack>

          {liste.Hinweise.length > 0 && (
            <Alert severity='info' sx={{ py: 0 }}>
              <Stack component='ul' sx={{ m: 0, pl: 2 }}>
                {liste.Hinweise.map(hinweis => (
                  <Typography component='li' variant='caption' key={hinweis}>
                    {hinweis}
                  </Typography>
                ))}
              </Stack>
            </Alert>
          )}

          {liste.Reason !== null && (
            <Typography variant='caption' color='text.secondary'>
              {liste.Reason}
            </Typography>
          )}

          {error !== null && <Alert severity='error'>{error}</Alert>}

          <Stack direction='row' spacing={1} justifyContent='flex-end'>
            <Button
              size='small'
              color='inherit'
              startIcon={<Close />}
              disabled={isPendingUebernehmeGesellschafter}
              onClick={() => setState('verworfen')}
            >
              Verwerfen
            </Button>
            <Button
              size='small'
              variant='contained'
              startIcon={
                isPendingUebernehmeGesellschafter ? (
                  <CircularProgress size={16} color='inherit' />
                ) : (
                  <Check />
                )
              }
              disabled={isPendingUebernehmeGesellschafter}
              onClick={() => void handleUebernehmen()}
            >
              Übernehmen
            </Button>
          </Stack>

          <Typography variant='caption' color='text.secondary'>
            Es wurde noch nichts angelegt. Die Angaben sind ausgelesen — bitte
            gegen die Liste prüfen.
          </Typography>
        </Stack>
      </Paper>
      {kontrollBlock}
    </>
  );
};
